import React, { useEffect, useMemo, useState } from "react";
import {
  ContractorVendorManagement,
  supplierFormData,
} from "../../../types/api";
import FileUpload from "../../../components/FileUpload/FileUpload";
import { useDropzone } from "react-dropzone";
import { useS3Upload } from "../../../utils/s3Client";
import { toast } from "react-toastify";
import { getAllSuppliers } from "../../../service/api";

interface CreateContractorVendorManagementProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: ContractorVendorManagement) => void;
  mode: "create" | "edit" | "view";
  initialData?: ContractorVendorManagement | null;
}

const defaultFormData: ContractorVendorManagement = {
  vendorName: "",
  phoneNumber: "",
  email: "",
  timelines: "",
  compliance: "",
  attachments: [],
};

const CreateContractorVendorManagement: React.FC<
  CreateContractorVendorManagementProps
> = ({ isOpen, onClose, onSubmit, mode, initialData }) => {
  const [formData, setFormData] =
    useState<ContractorVendorManagement>(defaultFormData);
  const [files, setFiles] = useState<File[] | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<
    ContractorVendorManagement["attachments"]
  >([]);
  const [suppliers, setSuppliers] = useState<supplierFormData[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>("");

  const { uploadFileToS3, progress, uploading } = useS3Upload();
  useEffect(() => {
    // fetch vendors (suppliers) from master data
    const fetchSuppliers = async () => {
      try {
        const res = await getAllSuppliers();
        setSuppliers(res.data.data || []);
      } catch {
        toast.error("Failed to load vendors");
      }
    };
    fetchSuppliers();

    if (initialData && (mode === "edit" || mode === "view")) {
      setFormData(initialData);
      setExistingAttachments(initialData.attachments || []);
      setFiles(null);
      // try to set selected vendor by matching name
      const match = (suppliers || []).find(
        (s: supplierFormData) =>
          s.name?.toLowerCase() === initialData.vendorName?.toLowerCase()
      );
      if (match?._id) {
        setSelectedVendorId(match._id);
      }
    } else if (mode === "create") {
      setFormData(defaultFormData);
      setExistingAttachments([]);
      setFiles(null);
      setSelectedVendorId("");
    }
  }, [initialData, mode, isOpen]);

  const isViewMode = mode === "view";

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (isViewMode) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    if (isViewMode) return;
    setFiles(newFiles);
  };

  const handleVendorChange = (value: string) => {
    if (isViewMode) return;
    setSelectedVendorId(value);
    const selected = suppliers.find(
      (s) => s._id === value || (s as any).id === value
    );
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        vendorName: selected.name,
        phoneNumber: selected.phoneNumber,
        email: selected.email,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        vendorName: "",
        phoneNumber: "",
        email: "",
      }));
    }
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (isViewMode) return;
    setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePreviewFile = (attachment: {
    url: string;
    name?: string;
    originalName?: string;
  }) => {
    if (!attachment.url) return;
    window.open(attachment.url, "_blank");
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (isViewMode) return;
    setFiles(acceptedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
    },
  });

  const disabled = isViewMode || uploading;

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
        isViewMode ? "bg-gray-100 cursor-not-allowed" : ""
      }`,
    [isViewMode]
  );

  const formatDateForInput = (value: string) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toISOString().split("T")[0];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) {
      onClose();
      return;
    }

    if (!formData.vendorName?.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    if (!formData.phoneNumber?.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!formData.email?.trim()) {
      toast.error("Email is required");
      return;
    }

    try {
      let uploadedAttachments = existingAttachments ?? [];

      if (files && files.length > 0) {
        const newAttachments = [];
        for (const file of files) {
          const url = await uploadFileToS3(file);
          newAttachments.push({
            url,
            name: file.name,
            uploadedAt: new Date().toISOString(),
          });
        }
        uploadedAttachments = [...uploadedAttachments, ...newAttachments];
      }

      const payload: ContractorVendorManagement = {
        ...formData,
        attachments: uploadedAttachments,
      };

      onSubmit?.(payload);
    } catch {
      toast.error("Failed to upload attachments");
      return;
    }

    onClose();
  };

  const handleCancel = () => {
    setFormData(defaultFormData);
    setFiles(null);
    setExistingAttachments([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {mode === "edit"
              ? "Edit Contractor / Vendor"
              : mode === "view"
              ? "View Contractor / Vendor"
              : "Add Contractor / Vendor"}
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Vendor Name (from master data) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <select
                  name="vendor"
                  value={selectedVendorId}
                  onChange={(e) => handleVendorChange(e.target.value)}
                  disabled={isViewMode}
                  className={inputClass}
                  required={!isViewMode}
                >
                  <option value="">Select Vendor</option>
                  {suppliers.map((vendor) => (
                    <option
                      key={vendor._id || vendor.id}
                      value={vendor._id || vendor.id}
                    >
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled
                  className={inputClass}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled
                  className={inputClass}
                />
              </div>

              {/* Timelines */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timeline Date
                </label>
                <input
                  type="date"
                  name="timelines"
                  value={formatDateForInput(formData.timelines)}
                  onChange={(e) =>
                    handleInputChange(e as React.ChangeEvent<HTMLInputElement>)
                  }
                  disabled={isViewMode}
                  className={inputClass}
                />
              </div>

              {/* Compliance */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Compliance Status
                </label>
                <select
                  name="compliance"
                  value={formData.compliance}
                  onChange={(e) =>
                    handleInputChange(e as React.ChangeEvent<HTMLSelectElement>)
                  }
                  disabled={isViewMode}
                  className={inputClass}
                >
                  <option value="">Select status</option>
                  <option value="Compliant">Compliant</option>
                  <option value="Non-Compliant">Non-Compliant</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>

            {/* Attachments */}
            <FileUpload
              mode={isViewMode ? "view" : mode}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments || []}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={progress}
              disabled={disabled}
              maxFileSizeMB={10}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-2 bg-[#61748e] border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
            disabled={uploading}
          >
            CANCEL
          </button>
          {mode === "view" ? null : (
            <button
              type="submit"
              onClick={handleSubmit}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
              disabled={uploading}
            >
              {mode === "edit" ? "UPDATE" : "SUBMIT"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateContractorVendorManagement;
